import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Archive, Lock, RefreshCw, RotateCcw, Trash2, Calendar, Package } from "lucide-react";
import { toast } from "sonner";
import { PasswordInput } from "@/components/ui/password-input";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorkOrderArchive = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState("");
  const [verifying, setVerifying] = useState(false);
  const [archivedOrders, setArchivedOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState(null);
  const [restorePassword, setRestorePassword] = useState("");
  const [restoring, setRestoring] = useState(false);
  
  // Delete states
  const [selectedIds, setSelectedIds] = useState([]);
  const [deleteTarget, setDeleteTarget] = useState(null); // Single delete
  const [showBulkDeleteDialog, setShowBulkDeleteDialog] = useState(false);
  const [deletePassword, setDeletePassword] = useState("");
  const [deleting, setDeleting] = useState(false);

  const verifyPassword = async () => {
    setVerifying(true);
    try {
      await axios.post(`${API}/work-orders/archived/verify-password`, { password });
      setIsAuthenticated(true);
      toast.success("Access granted");
      fetchArchived();
    } catch (err) {
      toast.error("Invalid password");
    } finally {
      setVerifying(false);
    }
  };

  const fetchArchived = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/work-orders/archived/list`);
      setArchivedOrders(res.data || []);
    } catch (err) {
      toast.error("Failed to load archived orders");
    } finally {
      setLoading(false);
    }
  };

  const handleRestore = async () => {
    if (!restorePassword) {
      toast.error("Enter password to restore");
      return;
    }
    setRestoring(true);
    try {
      await axios.post(`${API}/work-orders/archived/${restoreTarget.id}/restore`, {
        password: restorePassword,
      });
      toast.success(`${restoreTarget.header?.work_order_number} restored`);
      setRestoreTarget(null);
      setRestorePassword("");
      fetchArchived();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to restore");
    } finally {
      setRestoring(false);
    }
  };

  // Toggle selection for bulk delete
  const toggleSelection = (id) => {
    setSelectedIds(prev => 
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const toggleSelectAll = () => {
    if (selectedIds.length === archivedOrders.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(archivedOrders.map(wo => wo.id));
    }
  };

  // Delete single archived work order permanently
  const handleSingleDelete = async () => {
    if (!deletePassword) {
      toast.error("Enter password to delete");
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/work-orders/archived/${deleteTarget.id}/permanent`, {
        data: { password: deletePassword }
      });
      toast.success(`${deleteTarget.header?.work_order_number} permanently deleted`);
      setDeleteTarget(null);
      setDeletePassword("");
      fetchArchived();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  // Delete multiple archived work orders
  const handleBulkDelete = async () => {
    if (!deletePassword) {
      toast.error("Enter password to delete");
      return;
    }
    if (selectedIds.length === 0) {
      toast.error("No work orders selected");
      return;
    }
    setDeleting(true);
    try {
      await axios.post(`${API}/work-orders/archived/bulk-delete`, {
        password: deletePassword,
        ids: selectedIds
      });
      toast.success(`${selectedIds.length} work order(s) permanently deleted`);
      setShowBulkDeleteDialog(false);
      setDeletePassword("");
      setSelectedIds([]);
      fetchArchived();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete");
    } finally {
      setDeleting(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center p-6">
        <Card className="bg-black/80 border border-gray-700 w-full max-w-sm">
          <CardContent className="p-8 space-y-6">
            <div className="text-center">
              <Lock className="h-12 w-12 text-amber-400 mx-auto mb-3" />
              <h2 className="text-xl font-bold text-white">Work Order Archive</h2>
              <p className="text-gray-400 text-sm mt-1">Enter password to access deleted work orders</p>
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Password</Label>
              <PasswordInput
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && verifyPassword()}
                placeholder="Enter archive password"
                className="bg-gray-800 border-gray-600 text-white mt-1"
                data-testid="archive-password-input"
              />
            </div>
            <Button
              onClick={verifyPassword}
              disabled={verifying || !password}
              className="w-full bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="archive-unlock-btn"
            >
              {verifying ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Lock className="h-4 w-4 mr-2" />}
              Unlock Archive
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 p-6">
      <div className="max-w-5xl mx-auto space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black text-white flex items-center gap-3" data-testid="archive-title">
              <Archive className="h-8 w-8 text-amber-400" />
              Work Order Archive
            </h1>
            <p className="text-gray-400 mt-1">{archivedOrders.length} deleted work order(s)</p>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.length > 0 && (
              <Button
                onClick={() => {
                  setDeletePassword("");
                  setShowBulkDeleteDialog(true);
                }}
                variant="outline"
                size="sm"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete Selected ({selectedIds.length})
              </Button>
            )}
            <Button
              onClick={fetchArchived}
              variant="outline"
              size="sm"
              className="border-gray-600 text-gray-300 hover:bg-gray-800"
            >
              <RefreshCw className={`h-4 w-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <RefreshCw className="h-10 w-10 text-cyan-400 animate-spin" />
          </div>
        ) : archivedOrders.length === 0 ? (
          <Card className="bg-black/60 border border-gray-700">
            <CardContent className="p-12 text-center">
              <Trash2 className="h-12 w-12 mx-auto text-gray-600 mb-3" />
              <p className="text-gray-400">No deleted work orders found</p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {/* Select All */}
            {archivedOrders.length > 0 && (
              <div className="flex items-center gap-2 px-2">
                <Checkbox
                  checked={selectedIds.length === archivedOrders.length && archivedOrders.length > 0}
                  onCheckedChange={toggleSelectAll}
                  className="border-gray-500"
                />
                <span className="text-gray-400 text-sm">Select All</span>
              </div>
            )}
            {archivedOrders.map((wo) => {
              const header = wo.header || {};
              const isSelected = selectedIds.includes(wo.id);
              return (
                <Card key={wo.id} className={`bg-black/60 border ${isSelected ? 'border-amber-500/50' : 'border-gray-700'}`} data-testid={`archived-wo-${header.work_order_number}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4 flex-1">
                        <Checkbox
                          checked={isSelected}
                          onCheckedChange={() => toggleSelection(wo.id)}
                          className="border-gray-500"
                        />
                        <span className="text-amber-400 font-bold text-lg">{header.work_order_number}</span>
                        <span className="text-white">{header.customer_name}</span>
                        <span className="text-gray-400 text-sm">{header.item_name}</span>
                        {header.required_quantity && (
                          <Badge variant="outline" className="border-gray-600 text-gray-400 text-xs">
                            <Package className="h-3 w-3 mr-1" />
                            Qty: {header.required_quantity}
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <div className="text-right text-xs text-gray-500">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          Deleted: {wo.deleted_at ? new Date(wo.deleted_at).toLocaleDateString() : "N/A"}
                        </div>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-emerald-500/50 text-emerald-400 hover:bg-emerald-500/10"
                          onClick={() => {
                            setRestoreTarget(wo);
                            setRestorePassword("");
                          }}
                          data-testid={`restore-btn-${header.work_order_number}`}
                        >
                          <RotateCcw className="h-3 w-3 mr-1" />
                          Restore
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                          onClick={() => {
                            setDeleteTarget(wo);
                            setDeletePassword("");
                          }}
                          data-testid={`delete-btn-${header.work_order_number}`}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          Delete
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>

      {/* Restore Dialog */}
      <Dialog open={!!restoreTarget} onOpenChange={() => setRestoreTarget(null)}>
        <DialogContent className="bg-gray-900 border-gray-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-400 flex items-center gap-2">
              <RotateCcw className="h-5 w-5" />
              Restore Work Order
            </DialogTitle>
          </DialogHeader>
          {restoreTarget && (
            <div className="space-y-4">
              <div className="p-3 bg-gray-800/50 rounded-lg text-sm">
                <p className="text-gray-400">WO: <span className="text-white font-bold">{restoreTarget.header?.work_order_number}</span></p>
                <p className="text-gray-400">Customer: <span className="text-white">{restoreTarget.header?.customer_name}</span></p>
              </div>
              <div>
                <Label className="text-gray-400 text-sm">Confirm Password</Label>
                <PasswordInput
                  value={restorePassword}
                  onChange={(e) => setRestorePassword(e.target.value)}
                  placeholder="Enter archive password"
                  className="bg-gray-800 border-gray-600 text-white mt-1"
                  data-testid="restore-password-input"
                />
              </div>
              <div className="flex gap-3">
                <Button
                  onClick={handleRestore}
                  disabled={restoring}
                  className="flex-1 bg-emerald-600 hover:bg-emerald-700 text-white"
                  data-testid="restore-confirm-btn"
                >
                  {restoring ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <RotateCcw className="h-4 w-4 mr-2" />}
                  Restore Work Order
                </Button>
                <Button variant="ghost" onClick={() => setRestoreTarget(null)} className="text-gray-400">
                  Cancel
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Single Delete Dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent className="bg-gray-900 border-red-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Permanently Delete Work Order
            </DialogTitle>
          </DialogHeader>
          {deleteTarget && (
            <div className="space-y-4">
              <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm">
                <p className="text-red-300 font-semibold mb-2">⚠️ This action cannot be undone!</p>
                <p className="text-gray-400">WO: <span className="text-white font-bold">{deleteTarget.header?.work_order_number}</span></p>
                <p className="text-gray-400">Customer: <span className="text-white">{deleteTarget.header?.customer_name}</span></p>
              </div>
              <div>
                <Label className="text-gray-400 text-sm">Enter Password to Confirm</Label>
                <PasswordInput
                  value={deletePassword}
                  onChange={(e) => setDeletePassword(e.target.value)}
                  placeholder="Enter archive password"
                  className="bg-gray-800 border-gray-600 text-white mt-1"
                  data-testid="single-delete-password-input"
                />
              </div>
              <DialogFooter>
                <Button variant="ghost" onClick={() => setDeleteTarget(null)} className="text-gray-400">
                  Cancel
                </Button>
                <Button
                  onClick={handleSingleDelete}
                  disabled={deleting || !deletePassword}
                  className="bg-red-600 hover:bg-red-700 text-white"
                  data-testid="single-delete-confirm-btn"
                >
                  {deleting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                  Delete Permanently
                </Button>
              </DialogFooter>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Bulk Delete Dialog */}
      <Dialog open={showBulkDeleteDialog} onOpenChange={setShowBulkDeleteDialog}>
        <DialogContent className="bg-gray-900 border-red-500/50 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-400 flex items-center gap-2">
              <Trash2 className="h-5 w-5" />
              Delete {selectedIds.length} Work Order(s)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-sm">
              <p className="text-red-300 font-semibold mb-2">⚠️ This action cannot be undone!</p>
              <p className="text-gray-400">You are about to permanently delete <span className="text-white font-bold">{selectedIds.length}</span> work order(s).</p>
            </div>
            <div>
              <Label className="text-gray-400 text-sm">Enter Password to Confirm</Label>
              <PasswordInput
                value={deletePassword}
                onChange={(e) => setDeletePassword(e.target.value)}
                placeholder="Enter archive password"
                className="bg-gray-800 border-gray-600 text-white mt-1"
                data-testid="bulk-delete-password-input"
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowBulkDeleteDialog(false)} className="text-gray-400">
                Cancel
              </Button>
              <Button
                onClick={handleBulkDelete}
                disabled={deleting || !deletePassword}
                className="bg-red-600 hover:bg-red-700 text-white"
                data-testid="bulk-delete-confirm-btn"
              >
                {deleting ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Trash2 className="h-4 w-4 mr-2" />}
                Delete {selectedIds.length} Work Order(s)
              </Button>
            </DialogFooter>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default WorkOrderArchive;
